import { Component, inject, OnInit, signal } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { finalize } from 'rxjs';

import { LoadingIndicator } from '../../../../shared/loading-indicator/loading-indicator';
import { Session } from '../../../../services/session';
import { AcademicTermService } from '../../../../services/academic-term';
import { Helpers } from '../../../../utilities/helpers';
import { ApiEndpoints } from '../../../../core/api-endpoints';
import { FlashService } from '../../../../services/flash';
import { OnboardingService } from '../../../../services/school-onboarding';

@Component({
  selector: 'app-step-three',
  imports: [FormsModule, LoadingIndicator, RouterLink],
  templateUrl: './step-three.html',
  styleUrl: './step-three.css',
})
export class StepThree implements OnInit {
  private readonly flashService = inject(FlashService);

  private readonly onboardingService = inject(OnboardingService);

  protected startDate = '';
  protected endDate = '';
  protected termName = 'First Term';
  protected currentAcademicTerm = true;
  protected isLoading = signal(false);
  protected schoolId?: string;
  protected academicYearId?: string;
  protected academicYearName?: string;
  protected academicYearStartDate?: string;
  protected academicYearEndDate?: string;
  protected status: string = '';

  constructor(
    private readonly session: Session,
    private readonly router: Router,
    private readonly academicTermService: AcademicTermService,
  ) {
    const token = this.session.getItem('token');

    if (!token) {
      void this.router.navigate(['/']);
      return;
    }

    this.schoolId = this.session.decodeToken(token)?.school_id;

    if (!this.schoolId) {
      void this.router.navigate(['/']);
    }
  }

  ngOnInit(): void {
    const academicYear = this.onboardingService.getAcademicYear();

    if (!academicYear) {
      void this.router.navigate(['/school-onboarding/academic-year-configuration']);

      return;
    }

    this.academicYearId = academicYear.id;

    this.academicYearName = academicYear.name;

    this.academicYearStartDate = Helpers.formatDate(academicYear.start_date);

    this.academicYearEndDate = Helpers.formatDate(academicYear.end_date);

    const existingTerm = this.onboardingService.getTerm();

    if (!existingTerm) {
      return;
    }

    this.termName = existingTerm.name;

    this.startDate = this.formatDateForInput(existingTerm.start_date);

    this.endDate = this.formatDateForInput(existingTerm.end_date);
  }

  protected addTerm(form: NgForm): void {
    if (form.invalid) {
      this.flashService.failure('Fill all required fields.');

      form.control.markAllAsTouched();
      return;
    }

    if (!this.schoolId || !this.academicYearId) {
      void this.router.navigate(['/school-onboarding/academic-year-configuration']);

      return;
    }

    if (this.isLoading()) {
      return;
    }

    const existingTerm = this.onboardingService.getTerm();

    if (existingTerm) {
      void this.router.navigate(['/dashboard']);

      return;
    }

    this.isLoading.set(true);

    const academicTermForm = {
      school_id: this.schoolId,
      name: this.termName,
      start_date: this.startDate,
      end_date: this.endDate,
      academic_year_id: this.academicYearId,
      current_term: this.currentAcademicTerm,
      status: this.status,
    };

    this.academicTermService
      .createAcademicTerm(`${ApiEndpoints.url}/academic-term`, academicTermForm)
      .pipe(
        finalize(() => {
          this.isLoading.set(false);
        }),
      )
      .subscribe({
        next: (result) => {
          this.onboardingService.setTerm({
            id: result.payload.id,
            name: result.payload.term_name,
            start_date: result.payload.term_start_date,
            end_date: result.payload.term_end_date,
          });

          this.flashService.success('Added academic term successfully.');

          setTimeout(() => {
            this.onboardingService.reset();

            void this.router.navigate(['/dashboard']);
          }, 1500);
        },
        error: (err) => {
          this.flashService.failure(err?.error?.message ?? 'Could not add academic term. Try again.');
        },
      });
  }

  private formatDateForInput(value: string): string {
    return value ? value.substring(0, 10) : '';
  }
}
